import { memo, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BicepsFlexed, Camera, Check, CircleCheck, Pencil, Share2, Target, Trash2, X } from 'lucide-react';
import { CompletedByAvatars } from './CompletedByAvatars';
import { ClimbStandingIcon } from './ClimbStandingIcon';
import { ClimbLogActions, type ClimbLogAction } from './ClimbLogActions';
import { isClimbAuthor, useClimbAdminActions } from './useClimbAdminActions';
import { cropImageStyle, focusCrop, startHoldsBounds } from './hold-focus';
import { wantsPhoto } from './missing-photo';
import { useAddClimbPhoto } from './use-add-climb-photo';
import { ClimbPhotoSource } from './climb-editor/ClimbPhotoSource';
import { isDrawableLine } from './route-line';
import { SwipeAction, SwipeActions, SwipeContent, SwipeItem } from '../ui/swipe-actions';
import { useToast } from '../ui/use-toast';
import { useAuth } from '../../hooks/useAuth';
import { formatGradeOpinion } from '../climb-log/gradeUtils';
import { attemptsService } from '../../services/attempts';
import { climbsService } from '../../services/climbs';
import { trackEvent } from '../../utils/analytics';
import { climbHeading } from '../../utils/climb-title';
import { climbUrl } from '../../utils/climb-url';
import { climbShareUrl } from './climb-photo-effect';
import { BoardClimbThumbnail } from './board-grid/BoardClimbThumbnail';
import { climbWall } from './board-grid/climb-wall';
import type { Climb, UserAttemptData } from '../../types/climb';
import type { Board } from '../../types/device';

/** The thumbnail is square, so its crop is squared up to match. */
const THUMBNAIL_ASPECT = 1;

/**
 * The fewest photo pixels the thumbnail's crop may span. The box is 64 CSS px,
 * 128 device px on a phone, so a 96px crop is upscaled by a third at most —
 * still sharp. A start hold of twenty pixels zoomed to fill it was mush.
 */
const THUMBNAIL_MIN_CROP_PX = 96;

/**
 * How far the finger may wander before a press stops being a press. A drag
 * starts inside this, and a swipe held long enough must not open the lightbox
 * on top of the strip it just revealed.
 */
const LONG_PRESS_SLOP_PX = 10;

interface ClimbRowProps {
  climb: Climb;
  isStaffAdmin?: boolean;
  onClimbClick: (climb: Climb) => void;
  onClimbUpdated?: (updatedClimb: Climb) => void;
  onClimbDeleted?: (climbId: string) => void;
  onLongPress?: (climb: Climb) => void;
  /**
   * The board the list is under, which makes this a board problem's row: every
   * problem shares the board's photo, so the thumbnail is that photo zoomed to
   * where the problem starts (see BoardClimbThumbnail), and the grade moves
   * beside the name.
   */
  board?: Board | null;
  /** Board climbs on angle-changing boards show their inclination on the row. */
  showInclination?: boolean;
  /**
   * Went up since the reader last looked at this wall (or today): wears a "New"
   * label. Decided by the list, which knows the visit — see `isNewClimb`.
   */
  isNew?: boolean;
}

/**
 * The "New" tag: on the thumbnail's corner where there is one, at the end of
 * the heading where there is not.
 */
function NewLabel({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid="climb-new-label"
      className={`rounded bg-primary px-1 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-primary-foreground ${className}`}
    >
      {t('climbs.newLabel')}
    </span>
  );
}

/**
 * A row of a wall's list. Tap it for the climb; swipe it for what you would
 * otherwise go there to do.
 *
 * Swiped right, the row reveals the reader's own two actions — one more go,
 * and the send form — and a swipe all the way across opens the send form
 * without a tap. Swiped left it reveals, for the wall's admin, the buttons
 * that used to sit in a footer under every row (confirm and reject while it
 * is pending, edit and delete while it is up) and, for everybody, sharing —
 * which a swipe all the way across runs on its own. A footer on forty rows
 * was forty button bars; a strip is nothing until asked for.
 *
 * Memoized: a gym's list is a couple of hundred of these, and the list above
 * them re-renders on every filter, highlight and fade-out. A row only has to
 * follow its own climb and the handlers, which the list keeps stable.
 */
function ClimbRow({
  climb,
  isStaffAdmin = false,
  onClimbClick,
  onClimbUpdated,
  onClimbDeleted,
  onLongPress,
  board = null,
  showInclination = false,
  isNew = false,
}: ClimbRowProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPress = useRef(false);
  const pressOrigin = useRef<{ x: number; y: number } | null>(null);
  // Natural pixel shape of the photo, read off the <img> once it loads: the
  // crop below is squared up in pixels, and a box that is square in fractions
  // is only square on screen if the photo itself is.
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  // The send form and the rest of the log dialogs, mounted for this row only
  // once the reader has asked for them: they load the reader's log on mount,
  // and a wall of rows must not fire a wall of requests. Kept up afterwards,
  // so a second ask reopens on what the first one loaded.
  const [logAction, setLogAction] = useState<ClimbLogAction | null>(null);
  const [loggingTry, setLoggingTry] = useState(false);
  // Whether the two ways to a picture are on screen for this row — see the
  // camera standing in for the thumbnail below.
  const [addingPhoto, setAddingPhoto] = useState(false);

  const admin = useClimbAdminActions(climb, { onClimbUpdated, onClimbDeleted });
  const addPhoto = useAddClimbPhoto(climb);

  // The thumbnail zooms to where the climb starts instead of showing the whole
  // wall. At 64px a full photo of a boulder is a grey rectangle, while the start
  // holds are what tells one climb from the next on a crowded wall — the same
  // reading the route-setting board's cards use (see hold-focus).
  //
  // A route drawn as a line is left whole: it runs the height of the wall, and
  // cropping it anywhere throws away the shape that identifies it. So is a climb
  // whose holds were never marked, and the letter placeholder standing in for a
  // photo that does not exist.
  const startBounds = useMemo(
    () => (climb.image_medium_url && !isDrawableLine(climb.line) ? startHoldsBounds(climb.holds) : null),
    [climb.image_medium_url, climb.line, climb.holds]
  );
  const crop = useMemo(
    () => (startBounds && natural ? focusCrop(startBounds, natural.width, natural.height, THUMBNAIL_ASPECT, THUMBNAIL_MIN_CROP_PX) : null),
    [startBounds, natural]
  );

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    isLongPress.current = false;
    pressOrigin.current = { x: e.clientX, y: e.clientY };
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      onLongPress?.(climb);
    }, 500);
  };

  // A press that moves is a swipe (or a scroll), not a long press.
  const handlePointerMove = (e: React.PointerEvent) => {
    const origin = pressOrigin.current;
    if (!origin || !longPressTimer.current) return;
    if (Math.abs(e.clientX - origin.x) > LONG_PRESS_SLOP_PX || Math.abs(e.clientY - origin.y) > LONG_PRESS_SLOP_PX) {
      cancelLongPress();
    }
  };

  const handlePointerUp = () => {
    cancelLongPress();
    pressOrigin.current = null;
  };

  const handleClick = () => {
    if (isLongPress.current) {
      isLongPress.current = false;
      return;
    }
    onClimbClick(climb);
  };

  // The row is focusable so the strips open from the keyboard too (the arrow
  // keys, handled by the item); Enter then does what a tap does.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget || e.key !== 'Enter') return;
    e.preventDefault();
    onClimbClick(climb);
  };

  // One more go on it, dated today — no sheet, no question: the swipe is the
  // whole gesture. The row's standing turns to "attempted" at once, from the
  // attempt the server sent back; the list's own refetch confirms it later.
  const handleLogTry = async () => {
    if (loggingTry) return;
    try {
      setLoggingTry(true);
      const { attempt } = await attemptsService.createAttempt(climb.slug, { attempt_type: 'try' });
      const logged: UserAttemptData = {
        id: attempt.id,
        type: 'attempt',
        attributes: { attempt_type: attempt.attempt_type, attempted_at: attempt.attempted_at, created_at: new Date().toISOString() },
      };
      onClimbUpdated?.({ ...climb, user_attempts: [...(climb.user_attempts ?? []), logged] });
      trackEvent('attempt_logged', { climb_slug: climb.slug, attempt_type: 'try', count: 1 });
      toast({ title: t('attempts.createSuccess') });
    } catch (error) {
      console.error('Failed to create attempt:', error);
      toast({ title: t('attempts.createError'), variant: 'destructive' });
    } finally {
      setLoggingTry(false);
    }
  };

  // The send form: on the send already logged, if there is one, so "Libera" on
  // a sent climb is how the reader corrects it rather than a way to log it twice.
  const handleLogSend = () => {
    setLogAction((current) => ({
      kind: climb.user_topped ? 'edit_send' : 'send',
      nonce: (current?.nonce ?? 0) + 1,
    }));
  };

  // The link to share: none for a climb the API sent without a location,
  // which has no page of its own (see ClimbSerializer#location_data).
  const shareUrl = climb.location ? climbShareUrl(`${window.location.origin}${climbUrl(climb)}`, climb) : null;

  // The OS share sheet with the climb's link, or the clipboard where there is
  // none — the same share the climb's own page offers. Counted like the share
  // editor's, so the wall's dashboard sees it.
  const handleShare = async () => {
    if (!shareUrl) return;
    const url = shareUrl;
    try {
      if (navigator.share) {
        await navigator.share({
          title: displayTitle,
          text: t('climbProfile.shareText', { name: displayTitle, difficulty: climb.difficulty }),
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast({
          title: t('climbProfile.linkCopied'),
          description: t('climbProfile.linkCopiedDescription'),
          duration: 3000,
        });
      }
      climbsService.recordShare(climb.id);
    } catch (error) {
      // Backing out of the share sheet is not a failure.
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to share:', error);
        toast({
          title: t('errors.shareFailed'),
          description: t('errors.shareFailedDescription'),
          variant: 'destructive',
        });
      }
    }
  };

  const isClimbUnofficial = climb.official_status !== 'confirmed';

  const completedByCount = climb.sends?.length ?? 0;
  const completedByUsers = (climb.sends ?? [])
    .slice(0, 3)
    .map(send => send.attributes.user);

  // What the crowd made of it, out of five. 2.5 — the middle of the scale —
  // stands in until somebody votes, so the stars are always there to read.
  const rating = climb.average_rating ?? 2.5;

  // The yellow edge is the one mark of a climb not yet confirmed: the badge
  // that used to spell it out went with the footer it sat in.
  const unofficialBorder = isClimbUnofficial ? 'border-l-4 border-l-yellow-500' : '';

  const showAdminActions = isStaffAdmin && admin.available;
  // Edit and delete go to the climb's author as well: a climber logs most of a
  // gym's wall themselves, and should not have to ask the gym to fix or take
  // down what they put up. Confirm and reject stay the admin's.
  const managesClimb = isStaffAdmin || isClimbAuthor(climb, user);

  // What the climbers who logged it thought of the grade, as the ".x" the grade
  // pill used to carry on its end: .5 is "as graded", lower is soft, higher is
  // hard. On its own here, since the grade itself now heads the row — and only
  // once somebody has said something: null is "no opinion yet", not ".5".
  const gradeOpinion = climb.average_grade_opinion != null
    ? formatGradeOpinion(null, climb.average_grade_opinion)
    : null;

  // The row is headed by what people actually call the climb — the grade, the
  // holds, the sector — and its name, if it was given one, goes underneath in
  // small. See climbHeading, which every card that shows a climb reads from.
  const { heading, name: nameUnderneath } = climbHeading(climb);
  const displayTitle = heading || t('climbs.noGrade');

  // Whether there is a picture in the box at all: the same answer the `src`
  // below is written on, and what tells a thumbnail from a placeholder.
  const hasThumbnail = !!climb.image_medium_url;

  /**
   * A climb the gym put up as a line in a list, with the picture left to whoever
   * climbs it (see `wantsPhoto`) — so the empty box holds a camera instead of the
   * letter it used to.
   *
   * It is a real way in, not a hint: pressing it asks which source, and the
   * picture then opens in the editor, where the holds are circled. Offered to
   * everybody and not only the staff, because the person who can photograph a
   * wall is the person standing at it.
   */
  const boardRow = !!board;
  const offersPhoto = !boardRow && !hasThumbnail && wantsPhoto(climb);

  // A board problem drawn on its wall, where there is a photo and a hold to
  // zoom to; the grade in the box otherwise, as on a wall with no photo yet.
  const wall = useMemo(
    () => (board && climb.type === 'BoardClimb' ? climbWall(climb, board) : null),
    [board, climb]
  );
  const drawsWall = !!wall?.imageUrl && wall.holds.length > 0;
  const grade = climb.grade_visible ? climb.difficulty : null;

  return (
    <>
      <SwipeItem
        data-testid="climb-row"
        className={`border-b ${unofficialBorder}`}
      >
        {/* Swipe right: the reader's own log. Nothing for a visitor, who has
            none. The send is the outermost action, so a swipe all the way
            across opens its form: the thing a climber most wants to say about
            a climb, one gesture away. The go sits inside it, a tap away. */}
        {user && (
          <SwipeActions side="left" fullSwipe data-testid="climb-row-log-actions">
            <SwipeAction
              onClick={() => void handleLogTry()}
              disabled={loggingTry}
              className="bg-orange-500 text-white"
              data-testid="climb-row-action-try"
            >
              <Target />
              {t('climbs.swipe.try')}
            </SwipeAction>
            <SwipeAction
              onClick={handleLogSend}
              className="bg-green-600 text-white"
              data-testid="climb-row-action-send"
            >
              <CircleCheck />
              {t('climbs.swipe.send')}
            </SwipeAction>
          </SwipeActions>
        )}

        {/* Swipe left: for the wall's admin the buttons the footer used to
            hold (edit and delete for the climb's author too), then sharing at the far edge — the outermost action, so a
            swipe all the way across shares. Sharing is the one thing here safe
            to run on a gesture alone; the admin's buttons stay a tap away,
            and delete has its confirm besides. Narrow, since a pending climb
            has five of them to fit beside its content. */}
        <SwipeActions side="right" fullSwipe data-testid="climb-row-more-actions">
          {showAdminActions && admin.isPending && (
            <>
              <SwipeAction
                onClick={() => void admin.confirm()}
                disabled={admin.loading !== null}
                className="bg-green-600 text-white"
                contentClassName="min-w-14 px-1"
                data-testid="climb-row-action-confirm"
              >
                <Check />
                {t('climbs.admin.confirm')}
              </SwipeAction>
              <SwipeAction
                onClick={() => void admin.reject()}
                disabled={admin.loading !== null}
                className="bg-yellow-500 text-white"
                contentClassName="min-w-14 px-1"
                data-testid="climb-row-action-reject"
              >
                <X />
                {t('climbs.admin.reject')}
              </SwipeAction>
            </>
          )}
          {managesClimb && !admin.isDiscarded && (
            <>
              <SwipeAction
                onClick={admin.edit}
                disabled={admin.loading !== null}
                contentClassName="min-w-14 px-1"
                data-testid="climb-row-action-edit"
              >
                <Pencil />
                {t('climbs.admin.edit')}
              </SwipeAction>
              <SwipeAction
                onClick={() => void admin.destroy()}
                disabled={admin.loading !== null}
                className="bg-destructive text-destructive-foreground"
                contentClassName="min-w-14 px-1"
                data-testid="climb-row-action-destroy"
              >
                <Trash2 />
                {t('climbs.admin.destroy')}
              </SwipeAction>
            </>
          )}
          {shareUrl && (
            <SwipeAction
              onClick={() => void handleShare()}
              className="bg-primary text-primary-foreground"
              contentClassName="min-w-14 px-1"
              data-testid="climb-row-action-share"
            >
              <Share2 />
              {t('climbProfile.share')}
            </SwipeAction>
          )}
        </SwipeActions>

        {/* Main content. Tapping it opens the climb, unless the row is open —
            then the tap closes it, which the strip sees to before this hears
            of it. */}
        <SwipeContent
          data-testid="climb-row-content"
          className="flex flex-col gap-2 p-2 cursor-pointer transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* The heading has a row of its own, across the whole card: "6b · tacche
              arancioni · Grotta" is a sentence, not a label, and squeezed into the
              column beside the photo it was cut off after the grade — which is the
              one part of it nobody needs spelled out. */}
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-medium text-base min-w-0 flex-1 truncate">{displayTitle}</h3>
            {showInclination && climb.type === 'BoardClimb' && climb.inclination != null && (
              <span className="shrink-0 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                {climb.inclination}°
              </span>
            )}
            {/* The grade, on a board row whose box holds the wall rather than
                it: on a list headed by names, it is what tells one problem from
                the next after the holds. */}
            {drawsWall && grade && (
              <span
                data-testid="climb-row-grade-chip"
                className="shrink-0 rounded-md border px-2 py-0.5 text-xs font-semibold"
              >
                {grade}
              </span>
            )}
            {/* A board row keeps its "New" tag here, beside the standing: its
                thumbnail is all start holds, and a tag on them hides the one
                thing it is there to show. */}
            {isNew && boardRow && <NewLabel className="shrink-0" />}
            {/* At the end of the heading, in the corner the eye lands on first:
                where the reader stands on it — untried, a project, or sent and
                how. One glyph rather than the "To try / 3 attempts / Flash" badge
                this used to be, which on a wall of forty rows was forty labels.
                Nothing for a visitor, who stands nowhere on anything. */}
            {user && <ClimbStandingIcon climb={climb} />}
          </div>

          {/* Stretched, not top-aligned: the column beside the photo is then at
              least as tall as the photo, and its last line can sit on the
              bottom edge (`mt-auto` below) rather than wherever the text above
              it happens to stop. */}
          <div className="flex items-stretch gap-3">
            {/* A board's problems all share the one photo of the wall, so the
                box shows it zoomed to the problem's start; without a photo or
                holds to zoom to, it holds the grade instead. (A photo row
                carries its grade in the heading.) */}
            {wall && drawsWall ? (
              <BoardClimbThumbnail wall={wall} />
            ) : boardRow ? (
              <div
                data-testid="climb-row-grade"
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border bg-muted text-center text-lg font-bold"
              >
                {grade ?? '—'}
              </div>
            ) : offersPhoto ? (
              /* The camera in the empty box. It says what it does and then does
                 it: the two sources are asked for in a dialog, and the picture
                 opens in the editor where the holds are circled. Dashed and in
                 the accent colour because it *is* a control — the restraint a
                 placeholder deserves would be a lie here. */
              <button
                type="button"
                data-testid="climb-row-add-photo"
                aria-label={t('climbs.addPhoto')}
                title={t('climbs.addPhoto')}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!addPhoto.signedIn) return addPhoto.signIn();
                  setAddingPhoto(true);
                }}
                className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-md border-2 border-dashed border-primary/50 text-primary transition-colors hover:border-primary hover:bg-primary/5"
              >
                <Camera className="size-6" aria-hidden />
                {isNew && <NewLabel className="absolute bottom-1 right-1" />}
              </button>
            ) : (
              // A tap on the picture is a tap on the row: it opens the climb,
              // whose page shows the photo big anyway. The preview stays on the
              // long press.
              <div className="w-16 h-16 shrink-0 relative overflow-hidden rounded-md">
                <img
                  data-testid="climb-row-image"
                  src={climb.image_medium_url || `https://placehold.co/400x250.png?text=${displayTitle.charAt(0)}`}
                  alt={displayTitle}
                  className={crop ? 'absolute max-w-none object-cover' : 'w-full h-full object-cover'}
                  // Blow the photo up so the crop alone fills the box, then
                  // slide it into place (see cropImageStyle). The crop already
                  // carries the box's aspect, so this scales evenly.
                  style={cropImageStyle(crop)}
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  onLoad={(e) => {
                    if (!startBounds) return;
                    const { naturalWidth, naturalHeight } = e.currentTarget;
                    if (naturalWidth && naturalHeight) setNatural({ width: naturalWidth, height: naturalHeight });
                  }}
                />
                {/* Bottom-right corner of the photo: out of the way of the
                    start holds the crop centres on, which sit low and left
                    as often as not, but never off the picture. */}
                {isNew && <NewLabel className="absolute bottom-1 right-1" />}
              </div>
            )}

            {/* Beside the photo: what is said about the climb and who sent it,
                then — on the bottom edge — who put it up and what it is worth. */}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <div className="flex items-start gap-3">
                {/* The name it was given, if it was given one — under the heading
                    rather than in place of it, because it is the part the setter
                    chose and the part nobody uses. Then what the crowd made of
                    the grade, and the types. */}
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  {nameUnderneath && (
                    <span className="text-sm text-muted-foreground truncate">{nameUnderneath}</span>
                  )}
                  {gradeOpinion && (
                    <div
                      className="flex items-center gap-1 text-xs text-muted-foreground"
                      title={t('climbs.averageGradeOpinion')}
                    >
                      <BicepsFlexed className="w-3 h-3" aria-hidden />
                      <span>{gradeOpinion}</span>
                    </div>
                  )}
                  {climb.climb_types && climb.climb_types.length > 0 && (
                    <div className="hidden md:flex items-center gap-1 flex-wrap">
                      {climb.climb_types.map((type) => (
                        <span key={type} className="text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                          {t(`climbTypes.${type}`)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Who has sent it — up to 3 faces, then a "+X", captioned so
                    the faces are not mistaken for the setter's. Nothing at all
                    until somebody has: a caption over no faces would be a
                    claim over nothing. The age the corner used to carry is
                    gone: the "New" tag says the one thing about it anybody
                    acted on. */}
                {completedByCount > 0 && (
                  <div data-testid="climb-row-sent-by" className="flex shrink-0 items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{t('climbs.sentBy')}</span>
                    <CompletedByAvatars users={completedByUsers} totalCount={completedByCount} />
                  </div>
                )}
              </div>

              {/* The bottom line: the setter on the left, the stars on the
                  right. The stars are always there — 2.5, the middle of the
                  scale, when nobody has voted — so every row ends the same way
                  and a wall with no ratings yet still reads as a wall. */}
              <div className="mt-auto flex items-center gap-2 text-xs text-muted-foreground">
                {climb.route_setter && (
                  <span data-testid="climb-row-setter" className="flex min-w-0 items-center gap-1.5">
                    {/* Only an account has a face; a guest setter is the name alone. */}
                    {climb.route_setter.avatar_small_url && (
                      <img
                        src={climb.route_setter.avatar_small_url}
                        alt=""
                        className="h-4 w-4 shrink-0 rounded-full object-cover"
                      />
                    )}
                    <span className="truncate font-medium">{climb.route_setter.name}</span>
                  </span>
                )}
                <span
                  data-testid="climb-row-rating"
                  className="ml-auto flex shrink-0 items-center gap-0.5"
                  title={t('climbs.averageRating')}
                >
                  {[1, 2, 3, 4, 5].map((star) => {
                    const filled = rating >= star;
                    const half = !filled && rating >= star - 0.5;
                    return (
                      <span key={star} className="relative inline-block w-3 h-3">
                        <svg viewBox="0 0 24 24" className="w-3 h-3 text-gray-200 fill-current absolute inset-0">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                        </svg>
                        {(filled || half) && (
                          <svg viewBox="0 0 24 24" className="w-3 h-3 fill-yellow-400 text-yellow-400 absolute inset-0" style={half ? { clipPath: 'inset(0 50% 0 0)' } : undefined}>
                            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                          </svg>
                        )}
                      </span>
                    );
                  })}
                  <span className="ml-0.5">{rating}</span>
                </span>
              </div>
            </div>
          </div>
        </SwipeContent>
      </SwipeItem>

      {/* Outside the row rather than in it: the dialogs portal to the body,
          but their clicks still bubble up the React tree, and inside the row
          every one of them would open the climb. */}
      {offersPhoto && (
        <ClimbPhotoSource
          open={addingPhoto}
          onOpenChange={setAddingPhoto}
          // Not "change the photo": there is none to change.
          title={t('climbs.addPhoto')}
          // The shortcuts the editor offers are the place's own — a sector's
          // photo, the walls this gym was last shot on. Neither belongs on a row.
          zones={[]}
          recent={[]}
          onZonePhoto={() => {}}
          onRecentPhoto={() => {}}
          onFile={addPhoto.take}
        />
      )}

      {user && logAction && (
        <ClimbLogActions
          climb={climb}
          action={logAction}
          dateScope={climb.location?.slug}
          onLogChange={(log) => onClimbUpdated?.({
            ...climb,
            user_topped: log.topped,
            user_send_type: log.send_type ?? null,
            user_ascent_log_attempts_count: log.attempts_count,
          })}
        />
      )}
    </>
  );
}

export default memo(ClimbRow);
